import React, { memo, useCallback } from 'react'
import { TtTrayItem, TtTrayBreaker } from '@23/react'
import { useSortable } from '@dnd-kit/sortable'
import Toolbar from './Toolbar'
import GutterMenu from './GutterMenu'
import useTrayTracking from './useTrayTracking'
import useTrayStore from './useTrayStore'
import { useScrollRegister } from './ScrollProvider'
import { TrayCardBreaker } from '../../../firebase/model/tray-cards/types/breaker-model'
import { removeCard, updateBreakerCard } from '../../../firebase/hooks/useTray'

interface BreakerCardProps {
  isPriorToSelectedCard: boolean
  isLastCard: boolean
  position: number
  card: TrayCardBreaker
}

const BreakerCard = ({
  isPriorToSelectedCard,
  isLastCard,
  position,
  card,
}: BreakerCardProps): JSX.Element => {
  const trayTracking = useTrayTracking()
  const isCurrentlyEditing = useTrayStore(
    (state) => state.currentlyEditing === card.id
  )
  const setCurrentlyEditing = useTrayStore((state) => state.setCurrentlyEditing)
  const clearCurrentlyEditing = useTrayStore(
    (state) => state.clearCurrentlyEditing
  )
  useScrollRegister(card.id, position)

  const { setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: card.id,
    })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const onLabelChangeHandler = (value: string) => {
    updateBreakerCard(card.id, value)
    clearCurrentlyEditing()
  }

  const getGutterMode = useCallback(() => {
    if (isSorting) return 'disabled'
    if (isLastCard) return 'persisted'
    return 'normal'
  }, [isLastCard, isSorting])

  return (
    <TtTrayItem
      data-id={card.id}
      gutterDelay={400}
      ref={setNodeRef}
      style={style}
      gutterMode={getGutterMode()}
    >
      <TtTrayBreaker
        label={card.label}
        editing={isCurrentlyEditing}
        disabled={isPriorToSelectedCard}
        onTt-change={(e) => onLabelChangeHandler(e.detail.value)}
      >
        <Toolbar
          cardId={card.id}
          onEditClick={() => {
            trayTracking.startedEdit(card.id)
            setCurrentlyEditing(card.id)
          }}
          onDeleteClick={() => {
            trayTracking.deleted(card.id)
            removeCard(card.id)
          }}
        />
      </TtTrayBreaker>
      <GutterMenu cardId={card.id} />
    </TtTrayItem>
  )
}

export default memo(BreakerCard)
